import React, { useState, useEffect } from 'react';
import { testimonials } from '../data/mockData';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from './ui/button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const section = useScrollAnimation({ threshold: 0.15 });

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 400);
    }, 6000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const handleNav = (dir) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + dir + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 400);
  };

  const t = testimonials[currentIndex];

  return (
    <section id="testimonials" data-testid="testimonials-section" ref={section.ref} className="py-24 md:py-32 bg-[#0A0A12] relative overflow-hidden">
      <Quote className="absolute -top-8 -left-8 w-64 h-64 text-amber-500 opacity-[0.03] rotate-180" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${section.isVisible ? 'scroll-visible' : ''}`}>
          <span className="text-xs tracking-[0.2em] uppercase text-amber-500 mb-3 block font-bold">Real Results</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-white mb-4 uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
            They Waited. Then They <span className="text-gradient-amber">Didn't.</span>
          </h2>
          <p className="text-base text-slate-300 leading-relaxed">
            Real stories from business owners who stopped risking their brands and started protecting them.
          </p>
        </div>

        <div className={`max-w-3xl mx-auto scroll-scale-up scroll-delay-2 ${section.isVisible ? 'scroll-visible' : ''}`}>
          <div
            data-testid="testimonial-card"
            className="backdrop-blur-2xl bg-white/5 border border-white/10 rounded-xl p-10 md:p-14 text-center shadow-[0_8px_32px_rgba(0,0,0,0.4)] relative"
            style={{ opacity: isAnimating ? 0.5 : 1, transition: 'opacity 0.4s' }}
          >
            <div className="flex items-center justify-center gap-1 mb-8">
              {[...Array(t.rating)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
              ))}
            </div>

            <blockquote className="text-lg font-medium text-white/90 leading-relaxed mb-10 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              &ldquo;{t.content}&rdquo;
            </blockquote>

            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t.name.charAt(0)}
              </div>
              <div className="text-left">
                <div className="font-bold text-white">{t.name}</div>
                <div className="text-sm text-slate-400">{t.role} &middot; {t.service}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-6 mt-10">
            <Button data-testid="testimonial-prev" onClick={() => handleNav(-1)} disabled={isAnimating} variant="outline" className="rounded-md w-11 h-11 p-0 border-white/10 bg-transparent hover:bg-white/5 hover:border-amber-500/50 text-slate-400 hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button key={index} data-testid={`testimonial-dot-${index}`} onClick={() => { if (!isAnimating) { setIsAnimating(true); setCurrentIndex(index); setTimeout(() => setIsAnimating(false), 400); } }} className={`transition-all duration-300 rounded-sm ${index === currentIndex ? 'w-8 h-2 bg-gradient-to-r from-amber-500 to-orange-500' : 'w-2 h-2 bg-white/20 hover:bg-white/30'}`} />
              ))}
            </div>
            <Button data-testid="testimonial-next" onClick={() => handleNav(1)} disabled={isAnimating} variant="outline" className="rounded-md w-11 h-11 p-0 border-white/10 bg-transparent hover:bg-white/5 hover:border-amber-500/50 text-slate-400 hover:text-white">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
